/**
 * 对于链接层逻辑 @tencent/docs-connection 做一层简单封装
 */
import {
    ConnectionController,
    Command,
    IRoomSendMessagePacket,
    IConnectionCallBackResult,
    IConnectionControllerOptions,
} from "@tencent/docs-connection";
import { ILiveSocketMessageHandler, SendMsgResult } from "./websocket-client.interface";
import MsgFromServerHandler from "./server-message-handler";
import { IServerVars } from "../LiveCore";

const MAX_TASK_RANGE = 2147483648; // 4 byte 有符号数的最大值 + 1

class WebsocketClient {
    private connectionController: ConnectionController;
    private msgFromServerHandler: MsgFromServerHandler;
    private hasConnect = false;

    constructor(
        private onMessage: ILiveSocketMessageHandler,
        private module: string,
        private uid: string,
        private userId: string,
        private roomId: string,
        private serverVars: IServerVars,
        private useAllCookie: boolean,
        private isOwner: boolean,
    ) {
        this.msgFromServerHandler = new MsgFromServerHandler(this.onMessage, this);
        this.createConnectionController();
        this.setCallbackEventHandler();
    }

    /**
     * 创建一个 websocket 连接实例
     */
    private createConnectionController() {
        const { uid, userId } = this;
        const options: IConnectionControllerOptions = {
            serverVars: this.serverVars as IConnectionControllerOptions['serverVars'],
            uuId: uid, // 远程演示 uuId 用 uid 表示就可以
            userid: userId,
            module: this.module, // 自定义一个模块名称，比如‘Word’, ‘Sheet’, 'slide'
            uid,
            config: {
                isUseAllCookie: this.useAllCookie,
                location: {
                    path: "/websocket-show",
                },
                loginAdditionalHead: {
                    roomid: this.roomId,
                    isowner: this.isOwner as any,
                },
                heartBeatAdditionalHead: {
                    roomid: this.roomId,
                },
                customLoginMsgOptions: {},
            },
        };

        // 创建连接层
        this.connectionController = new ConnectionController(options);
    }

    /**
     * 打开 websocket 连接
     */
    public open() {
        if (!this.hasConnect) {
            this.connectionController.connect();
            this.hasConnect = true;
        }
    }

    /**
     * 关闭 websocket 连接
     */
    public close(reason?: string) {
        this.connectionController.disconnect({
            reason,
            shutDown: true,
        });
    }

    /**
     * 主动发送消息
     * @param cmd 消息的类型
     * @param data 消息携带的数据
     */
    public sendMessage(cmd: Command, data: string, callback: (result: SendMsgResult) => void) {
        const taskId = Math.ceil(Math.random() * MAX_TASK_RANGE);
        const message: IRoomSendMessagePacket = {
            cmd,
            taskId,
            data, // 远程演示消息体异化较多，直接传 json string
            additionalFieldsToHead: {
                roomid: this.roomId,
                isowner: this.isOwner,
            },
            // 错误不进行额外处理
            onError: () => {},
            // 注意此处的 onsend 参数类型是远程演示特有的, docs-connection 中并未声明
            onsend: (taskId, cmd, data, result: SendMsgResult ) => {
                callback(result)
            }
        };
        this.connectionController.sendMessage(message);
    }

    /**
     * 监听连接层回调事件
     */
    private setCallbackEventHandler() {
        this.connectionController.onConnectionEvent((result: IConnectionCallBackResult) => {
            this.msgFromServerHandler.handleCallbackEvent(result);
        });
    }
}

export default WebsocketClient;
